import type React from 'react';
import type { SxProps, Theme } from '@mui/material/styles';


import { Box } from 'ui/box';

import type { NormalizedBarChartData } from 'components/partials/charts/shared/types';
import { formatValue } from 'components/partials/charts/shared/format-value';

interface ChartPartTableProps {
  data: NormalizedBarChartData;
  caption: string;
}

const TABLE_SX: SxProps<Theme> = {
  width: '100%',
  minWidth: 0,
  borderWidth: '1px',
  borderColor: 'tokens.gray.200',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
};

const CELL_SX: SxProps<Theme> = {
  borderWidth: '1px',
  borderColor: 'tokens.gray.200',
  px: 1.5,
  py: 1,
  textAlign: 'left',
  overflowWrap: 'break-word',
};

const HEAD_CELL_SX: SxProps<Theme> = [
  CELL_SX,
  { fontWeight: 600, bgcolor: 'tokens.gray.50' },
];

const BODY_CELL_SX: SxProps<Theme> = [CELL_SX, { fontWeight: 400 }];

const ChartPartTable = ({
  data,
  caption,
}: ChartPartTableProps): React.JSX.Element => (
  <Box sx={{ mt: 1.5 }}>
    <Box component="table" sx={TABLE_SX}>
      <caption className="sr-only">{caption}</caption>
      <thead>
        <tr>
          <Box component="th" scope="col" sx={HEAD_CELL_SX}>
            {data.categoryAxis.label || 'Category'}
          </Box>
          {data.series.map((singleSeries, index) => (
            <Box
              component="th"
              key={`${singleSeries.label}-${index}`}
              scope="col"
              sx={HEAD_CELL_SX}
            >
              {singleSeries.label}
            </Box>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.categoryAxis.categories.map((category, categoryIndex) => (
          <tr key={`${category}-${categoryIndex}`}>
            <Box component="th" scope="row" sx={BODY_CELL_SX}>
              {category}
            </Box>
            {data.series.map((singleSeries, seriesIndex) => (
              <Box
                component="td"
                key={`${singleSeries.label}-${seriesIndex}-${categoryIndex}`}
                sx={BODY_CELL_SX}
              >
                {formatValue(singleSeries.values[categoryIndex], data.format)}
              </Box>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
);

export default ChartPartTable;
